/**
 * Session-scoped flags and Ink variable mirror.
 *
 * Playtime tracking lives on `PartyStore` (P14) — it is state-gated so the
 * counter pauses during dialogue/menu rather than running off wall-clock time.
 */

const questFlags = new Map<string, boolean>();
const inkVariables = new Map<string, unknown>();

export interface SessionStoreJSON {
  questFlags: Record<string, boolean>;
  inkVariables: Record<string, unknown>;
}

export const SessionStore = {
  setQuestFlag(flag: string, value: boolean): void {
    questFlags.set(flag, value);
  },

  getQuestFlag(flag: string): boolean {
    return questFlags.get(flag) ?? false;
  },

  /**
   * Mirrors an Ink variable change so TypeScript code can react to narrative state.
   * Variables with a `quest_` prefix are also written to the typed quest-flag store (N05).
   */
  setInkVariable(name: string, value: unknown): void {
    inkVariables.set(name, value);
    if (name.startsWith("quest_")) {
      questFlags.set(name, Boolean(value));
    }
  },

  getInkVariable(name: string): unknown {
    return inkVariables.get(name);
  },

  reset(): void {
    questFlags.clear();
    inkVariables.clear();
  },

  toJSON(): SessionStoreJSON {
    return {
      questFlags: Object.fromEntries(questFlags),
      inkVariables: Object.fromEntries(inkVariables),
    };
  },

  loadJSON(data: SessionStoreJSON): void {
    questFlags.clear();
    for (const [k, v] of Object.entries(data.questFlags)) questFlags.set(k, v);
    inkVariables.clear();
    for (const [k, v] of Object.entries(data.inkVariables)) inkVariables.set(k, v);
  },
};
